import { DataTable } from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { SORTING_ALGORITHM_VISUALIZER_STRINGS } from "@/components/tools/sorting-algorithm-visualizer";
import { t } from "@/lib/i18n/interpolate";
import {
  ALGORITHM_IDS,
  type AlgorithmId,
  countsOf,
  type Mark,
  type Message,
  type Step,
  traceSort,
} from "@/lib/tool-kit/cs-tools/sorting";

export type Strings = typeof SORTING_ALGORITHM_VISUALIZER_STRINGS;
export type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const VALUE_FORMAT: Intl.NumberFormatOptions = {
  maximumFractionDigits: 3,
  useGrouping: false,
};
const LABELS_UP_TO = 24;

export const NAME_KEYS: Record<AlgorithmId, keyof Strings> = {
  bubble: "nameBubble",
  insertion: "nameInsertion",
  selection: "nameSelection",
  merge: "nameMerge",
  quick: "nameQuick",
  heap: "nameHeap",
};

const IDEA_KEYS: Record<AlgorithmId, keyof Strings> = {
  bubble: "ideaBubble",
  insertion: "ideaInsertion",
  selection: "ideaSelection",
  merge: "ideaMerge",
  quick: "ideaQuick",
  heap: "ideaHeap",
};

/** Time and space classes; symbolic, so they are the same in every language. */
const COMPLEXITY: Record<
  AlgorithmId,
  {
    best: string;
    average: string;
    worst: string;
    space: string;
    stable: boolean;
  }
> = {
  bubble: {
    best: "O(n)",
    average: "O(n²)",
    worst: "O(n²)",
    space: "O(1)",
    stable: true,
  },
  insertion: {
    best: "O(n)",
    average: "O(n²)",
    worst: "O(n²)",
    space: "O(1)",
    stable: true,
  },
  selection: {
    best: "O(n²)",
    average: "O(n²)",
    worst: "O(n²)",
    space: "O(1)",
    stable: false,
  },
  merge: {
    best: "O(n log n)",
    average: "O(n log n)",
    worst: "O(n log n)",
    space: "O(n)",
    stable: true,
  },
  quick: {
    best: "O(n log n)",
    average: "O(n log n)",
    worst: "O(n²)",
    space: "O(log n) – O(n)",
    stable: false,
  },
  heap: {
    best: "O(n log n)",
    average: "O(n log n)",
    worst: "O(n log n)",
    space: "O(1)",
    stable: false,
  },
};

const MARK_STYLES: Record<Mark, string> = {
  compare: "bg-amber-500",
  swap: "bg-red-500",
  write: "bg-sky-500",
  pivot: "bg-violet-500",
};

const MARK_GLYPHS: Record<Mark, string> = {
  compare: "?",
  swap: "⇄",
  write: "↓",
  pivot: "P",
};

function barClass(mark: Mark | undefined, settled: boolean, dim: boolean) {
  let colour = "bg-primary/40";
  if (mark) {
    colour = MARK_STYLES[mark];
  } else if (settled) {
    colour = "bg-emerald-500";
  }
  return `${colour} ${dim ? "opacity-40" : ""}`;
}

function BarRow({
  values,
  marks,
  settled,
  range,
  offset,
  min,
  max,
  fmt,
}: {
  values: readonly number[];
  marks: Record<number, Mark>;
  settled: ReadonlySet<number>;
  range: [number, number] | null;
  offset: number;
  min: number;
  max: number;
  fmt: Fmt;
}) {
  const span = max - min;
  const labels = values.length <= LABELS_UP_TO;
  return (
    <div aria-hidden="true" className="flex items-end gap-px">
      {values.map((value, index) => {
        const height = span === 0 ? 60 : 12 + (88 * (value - min)) / span;
        const mark = marks[index];
        const dim =
          range !== null &&
          (index + offset < range[0] || index + offset > range[1]);
        return (
          <div
            className="flex min-w-0 flex-1 flex-col items-center justify-end"
            // biome-ignore lint/suspicious/noArrayIndexKey: bars are positions in the array
            key={index}
          >
            <span className="h-4 font-mono text-foreground text-xs">
              {mark ? MARK_GLYPHS[mark] : ""}
            </span>
            <div
              className={`w-full rounded-t-sm ${barClass(mark, settled.has(index + offset), dim)}`}
              style={{ height: `${Math.round(height * 1.6)}px` }}
            />
            {labels ? (
              <span className="font-mono text-foreground text-xs">
                {fmt(value, VALUE_FORMAT)}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function Bars({
  step,
  strings,
  fmt,
}: {
  step: Step;
  strings: Strings;
  fmt: Fmt;
}) {
  const settled = new Set(step.settled);
  const all = step.array;
  const min = Math.min(...all);
  const max = Math.max(...all);
  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-3 sm:p-4">
      <BarRow
        fmt={fmt}
        marks={step.marks}
        max={max}
        min={min}
        offset={0}
        range={step.range}
        settled={settled}
        values={all}
      />
      {step.aux ? (
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs">
            {t(strings.auxCaption, {
              lo: fmt(step.aux.offset + 1, VALUE_FORMAT),
              hi: fmt(step.aux.offset + step.aux.values.length, VALUE_FORMAT),
            })}
          </p>
          <BarRow
            fmt={fmt}
            marks={step.aux.marks}
            max={max}
            min={min}
            offset={step.aux.offset}
            range={null}
            settled={new Set()}
            values={step.aux.values}
          />
        </div>
      ) : null}
    </div>
  );
}

export function Legend({ strings }: { strings: Strings }) {
  const items: [Mark | "settled", string, string][] = [
    ["compare", MARK_GLYPHS.compare, strings.legendCompare],
    ["swap", MARK_GLYPHS.swap, strings.legendSwap],
    ["write", MARK_GLYPHS.write, strings.legendWrite],
    ["pivot", MARK_GLYPHS.pivot, strings.legendPivot],
    ["settled", "", strings.legendSettled],
  ];
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground text-xs">
      {items.map(([kind, glyph, label]) => (
        <li className="flex items-center gap-1.5" key={kind}>
          <span
            aria-hidden="true"
            className={`inline-block size-3 rounded-sm ${
              kind === "settled" ? "bg-emerald-500" : MARK_STYLES[kind]
            }`}
          />
          {glyph ? (
            <span aria-hidden="true" className="font-mono">
              {glyph}
            </span>
          ) : null}
          {label}
        </li>
      ))}
    </ul>
  );
}

const RELATION_KEYS = {
  gt: "relGt",
  eq: "relEq",
  lt: "relLt",
} as const;

export function describe(message: Message, strings: Strings, fmt: Fmt): string {
  const params: Record<string, string> = {};
  for (const [name, value] of Object.entries(message.params)) {
    params[name] = fmt(value, VALUE_FORMAT);
  }
  switch (message.key) {
    case "start":
      return t(strings.msgStart, params);
    case "compare": {
      const located =
        (message.params.at ?? 0) > 0 && (message.params.at2 ?? 0) > 0;
      const relation = strings[RELATION_KEYS[message.relation ?? "eq"]];
      const head = t(
        located ? strings.msgCompareAt : strings.msgCompare,
        params
      );
      return `${head} ${t(strings.msgCompareResult, { ...params, relation })}`;
    }
    case "newMin":
      return t(strings.msgNewMin, params);
    case "swap":
      return t(strings.msgSwap, params);
    case "write":
      return t(strings.msgWrite, params);
    case "settle":
      return t(strings.msgSettle, params);
    case "pivot":
      return t(strings.msgPivot, params);
    case "merge":
      return t(strings.msgMerge, params);
    case "heapBuild":
      return t(strings.msgHeapBuild, params);
    case "heapExtract":
      return t(strings.msgHeapExtract, params);
    case "noSwapsDone":
      return t(strings.msgNoSwaps, params);
    default:
      return t(strings.msgDone, params);
  }
}

export function FactsTable({
  id,
  strings,
}: {
  id: AlgorithmId;
  strings: Strings;
}) {
  const c = COMPLEXITY[id];
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>
        {t(strings.factsHeading, { name: strings[NAME_KEYS[id]] })}
      </h3>
      <p className="text-muted-foreground text-sm">{strings[IDEA_KEYS[id]]}</p>
      <DataTable
        caption={strings.factsCaption}
        head={[strings.colProperty, strings.colValue]}
        rows={[
          [strings.factBest, c.best],
          [strings.factAverage, c.average],
          [strings.factWorst, c.worst],
          [strings.factSpace, c.space],
          [strings.factStable, c.stable ? strings.yes : strings.no],
        ]}
      />
    </section>
  );
}

/** Final operation counts for every algorithm on one input. */
export function summarize(
  numbers: readonly number[]
): { id: AlgorithmId; comparisons: number; swaps: number; writes: number }[] {
  return ALGORITHM_IDS.map((id) => ({
    id,
    ...countsOf(traceSort(id, numbers)),
  }));
}

export function SummaryTable({
  summary,
  strings,
  fmt,
}: {
  summary: ReturnType<typeof summarize>;
  strings: Strings;
  fmt: Fmt;
}) {
  const plain: Intl.NumberFormatOptions = { maximumFractionDigits: 0 };
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.summaryHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.summaryBody}</p>
      <DataTable
        caption={strings.summaryCaption}
        head={[
          strings.colAlgorithm,
          strings.statComparisons,
          strings.statSwaps,
          strings.statWrites,
        ]}
        rows={summary.map((row) => [
          strings[NAME_KEYS[row.id]],
          fmt(row.comparisons, plain),
          fmt(row.swaps, plain),
          fmt(row.writes, plain),
        ])}
      />
    </section>
  );
}
